import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useStreamAuth } from '../../hooks/useStreamAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProfile } from '../../contexts/ProfileContext';
import ProfileDropdown from '../ProfileDropdown';
import AskArloPill from '../askArlo/AskArloPill';
import spotlightService from '../../services/SpotlightService';
import { cacheService } from '../../services/CacheService';
import { getHomepagePreference, getHomepageRoute } from '../../utils/HomepagePreference';

// Custom hook for header navigation preloading
const useHeaderPreload = (pageType, delay = 300) => {
  const timeoutRef = useRef(null);
  const hasPrefetched = useRef(false);

  const handleMouseEnter = useCallback(() => {
    if (hasPrefetched.current) return;

    timeoutRef.current = setTimeout(async () => {
      try {
        // Check if already cached to avoid redundant requests
        if (pageType === 'movies' && cacheService.hasMoviesPage()) {
          return;
        }
        if (pageType === 'series' && cacheService.hasSeriesPage()) {
          return;
        }

        // Preload different data based on page type
        switch (pageType) {
          case 'movies':
            await spotlightService.getMoviesHomePage().catch(e => {
              throw new Error(e);
            });
            cacheService.setMoviesPage({ timestamp: Date.now() });
            break;
          case 'series':
            await spotlightService.getSeriesHomePage().catch(e => {
              throw new Error(e);
            });
            cacheService.setSeriesPage({ timestamp: Date.now() });
            break;
          case 'live-tv':
            // For Live TV, we might want to preload EPG data or live channels
            // For now, we'll skip preloading as it's handled differently
            break;
          default:
            break;
        }
        hasPrefetched.current = true;
      } catch (error) {
        console.warn(`Header preload failed for ${pageType}:`, error);
      }
    }, delay);
  }, [pageType, delay]);

  const handleMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { handleMouseEnter, handleMouseLeave };
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading, loginWithRedirect, logout } = useStreamAuth();
  const { currentProfile, needsProfileSelection, hasLoadedProfiles } = useProfile();

  // Scroll state
  const [scrollY, setScrollY] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  // Set up preloading hooks for navigation buttons
  const moviesPreload = useHeaderPreload('movies', 0);
  const seriesPreload = useHeaderPreload('series', 0);
  const liveTVPreload = useHeaderPreload('live-tv', 0);

  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);

      // Hide header when scrolling down past 200px, show when scrolling up
      if (currentScrollY > 200) {
        if (currentScrollY > lastScrollY) {
          // Scrolling down
          setHeaderVisible(false);
        } else {
          // Scrolling up
          setHeaderVisible(true);
        }
      } else {
        // Always show header when near top
        setHeaderVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Dynamic header classes based on scroll position
  const getHeaderClasses = () => {
    const baseClasses = 'py-4 px-6 sticky top-0 z-30 transition-all duration-300';
    const visibilityClasses = headerVisible ? 'translate-y-0' : '-translate-y-full';
    // Always use the same gradient, even when scrolled
    return `${baseClasses} ${visibilityClasses} bg-gradient-to-b from-black/90 via-black/70 to-transparent`;
  };

  // Memoized navigation handlers
  const handleHomeClick = useCallback(() => {
    // Get user's homepage preference
    const profileId = currentProfile?._id || user?.sub;
    const preference = getHomepagePreference(profileId);
    const route = getHomepageRoute(preference);
    navigate(route);
  }, [navigate, currentProfile, user]);
  const handleMoviesClick = useCallback(() => navigate('/movies'), [navigate]);
  const handleSeriesClick = useCallback(() => navigate('/series'), [navigate]);
  const handleLiveTVClick = useCallback(() => navigate('/live-tv'), [navigate]);
  const handleMyListsClick = useCallback(() => navigate('/my-lists'), [navigate]);

  // Function to check if a path is active
  const isActive = useCallback(
    path => {
      if (path === '/') {
        return location.pathname === '/';
      }
      return location.pathname.startsWith(path);
    },
    [location.pathname],
  );

  // Auth handlers
  const handleLogin = useCallback(() => {
    loginWithRedirect({
      authorizationParams: {
        redirect_uri: window.location.origin,
        scope: 'openid profile email read:streams write:streams',
      },
    });
  }, [loginWithRedirect]);

  const handleLogout = useCallback(() => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }, [logout]);

  const handleUserClick = useCallback(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      handleLogin();
    }
  }, [isAuthenticated, navigate, handleLogin]);

  // Search handlers
  const handleSearchChange = useCallback(
    e => {
      const value = e.target.value;
      setSearchQuery(value);

      // Navigate to search page when user starts typing
      if (value.trim().length > 0) {
        navigate(`/search?q=${encodeURIComponent(value.trim())}`);
      }
    },
    [navigate],
  );

  const handleSearchFocus = useCallback(() => {
    setIsSearchExpanded(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    // Keep expanded if there's a search query
    if (!searchQuery.trim()) {
      setIsSearchExpanded(false);
    }
  }, [searchQuery]);

  const handleSearchKeyPress = useCallback(
    e => {
      if (e.key === 'Enter' && searchQuery.trim()) {
        navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      }
    },
    [navigate, searchQuery],
  );

  const logo = `${
    window.ASSETS_BASE_URL || window.location.origin
  }/assets/images/chillfree-logo.png`;

  return (
    <header className={getHeaderClasses()}>
      <div className="container flex items-center justify-between mx-auto">
        <div className="flex items-center">
          <img
            src={logo}
            alt="ChillFree"
            className="h-16 cursor-pointer"
            onClick={handleHomeClick}
          />
        </div>

        <nav className="hidden space-x-6 md:flex">
          <button
            className={`font-bold ${
              isActive('/') ? 'text-white' : 'text-white/70'
            } hover:text-[#56bdaf] bg-transparent border-0 p-0`}
            onClick={handleHomeClick}
          >
            Home
          </button>
          <button
            className={`font-bold ${
              isActive('/movies') ? 'text-white' : 'text-white/70'
            } hover:text-[#56bdaf] bg-transparent border-0 p-0`}
            onClick={handleMoviesClick}
            onMouseEnter={moviesPreload.handleMouseEnter}
            onMouseLeave={moviesPreload.handleMouseLeave}
          >
            Movies
          </button>
          <button
            className={`font-bold ${
              isActive('/series') ? 'text-white' : 'text-white/70'
            } hover:text-[#56bdaf] bg-transparent border-0 p-0`}
            onClick={handleSeriesClick}
            onMouseEnter={seriesPreload.handleMouseEnter}
            onMouseLeave={seriesPreload.handleMouseLeave}
          >
            Series
          </button>
          <button
            className={`font-bold ${
              isActive('/live-tv') ? 'text-white' : 'text-white/70'
            } hover:text-[#56bdaf] bg-transparent border-0 p-0`}
            onClick={handleLiveTVClick}
            onMouseEnter={liveTVPreload.handleMouseEnter}
            onMouseLeave={liveTVPreload.handleMouseLeave}
          >
            Live TV
          </button>
          <button
            className={`font-bold ${
              isActive('/my-lists') ? 'text-white' : 'text-white/70'
            } hover:text-[#56bdaf] bg-transparent border-0 p-0`}
            onClick={handleMyListsClick}
          >
            My Stuff
          </button>
        </nav>

        <div className="flex items-center space-x-6">
          <AskArloPill />

          {/* Hover-expandable search for desktop */}
          <div className="relative hidden md:block">
            <div
              className={`flex items-center transition-all duration-300 ease-in-out ${
                isSearchExpanded || searchQuery ? 'w-48 mr-12' : 'w-14'
              } h-10 group relative z-10`}
            >
              <button
                className="flex items-center justify-center w-10 h-10 transition-colors duration-200 text-white/80 hover:text-white"
                type="button"
                onMouseEnter={() => setIsSearchExpanded(true)}
                onMouseLeave={() => !searchQuery && setIsSearchExpanded(false)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
              <input
                type="text"
                placeholder="Search for movies, shows, and more..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                onKeyPress={handleSearchKeyPress}
                onMouseEnter={() => setIsSearchExpanded(true)}
                onMouseLeave={() => !searchQuery && setIsSearchExpanded(false)}
                className={`flex-1 px-3 py-2 text-sm text-white bg-gray-800/95 backdrop-blur-sm rounded-full border border-gray-600 outline-none transition-all duration-300 placeholder-white/50 ${
                  isSearchExpanded || searchQuery ? 'opacity-100' : 'opacity-0'
                }`}
              />
            </div>
          </div>

          {/* Mobile search icon */}
          <button
            className="text-white transition-colors duration-200 md:hidden hover:text-accent"
            onClick={() => navigate('/search')}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </button>

          {/* Authentication section */}
          <div className="relative z-20 flex items-center space-x-2">
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white rounded-full border-t-transparent animate-spin"></div>
            ) : isAuthenticated ? (
              <div className="flex items-center">
                {/* Profile Dropdown - Show if user has a current profile */}
                {currentProfile ? (
                  <ProfileDropdown />
                ) : hasLoadedProfiles && needsProfileSelection() ? (
                  /* Show profile selection prompt if profiles exist but none selected */
                  <button
                    onClick={() => navigate('/profiles')}
                    className="bg-accent hover:bg-accent/80 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    Select Profile
                  </button>
                ) : (
                  /* Fallback user button for when profiles haven't loaded yet */
                  <button
                    className="flex items-center space-x-2 text-white hover:text-accent"
                    onClick={handleUserClick}
                  >
                    {user?.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name || 'User'}
                        className="w-8 h-8 border-2 rounded-full border-accent"
                      />
                    ) : (
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-accent">
                        <span className="text-sm font-semibold text-white">
                          {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                        </span>
                      </div>
                    )}
                    <span className="hidden text-sm md:block">
                      {user?.name || user?.email || 'User'}
                    </span>
                  </button>
                )}
              </div>
            ) : (
              <button
                onClick={handleLogin}
                className="bg-[#56bdaf] hover:bg-[#4aa89c] text-white px-4 py-2 rounded-full text-sm font-medium transition-colors"
              >
                Log In
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
